import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { Card, EmptyState, Skeleton } from '@/ui/primitives'
import { lei } from '@/lib/money'

type Tva = {
  period: { from: string; to: string }
  summary: {
    total_sales_ron: number; total_costs_ron: number; comision_brut: number
    tva_datorat: number; comision_net: number; sales_count: number
    purchase_count: number; effective_vat_rate: number; duplicates_removed: number
  }
  monthly: { month: string; sales: number; costs: number; comision_brut: number
    tva: number; comision_net: number; sales_count: number; purchase_count: number }[]
  note?: string
}
type RaportAvansuri = {
  totals: { total_advances: number; total_supplier_unpaid: number
    total_client_collected: number; count: number }
  advances: { booking_number: string; client: string; client_invoice: string | null
    client_total_ron: number; client_collected_ron?: number }[]
}

const luna = (s: string) => {
  const [an, l] = s.split('-')
  return new Date(Number(an), Number(l) - 1).toLocaleDateString('ro-RO', { month: 'long', year: 'numeric' })
}

export function RapoarteBani() {
  const tva = useQuery({ queryKey: ['raport-tva'], queryFn: () => api.get<Tva>('/reports/vat-owed') })
  const av = useQuery({ queryKey: ['raport-avansuri'], queryFn: () => api.get<RaportAvansuri>('/reports/advances') })

  return (
    <>
      <div className="px-8 py-6 space-y-8">
        <section>
          <h2 className="text-[13px] font-semibold tracking-[0.08em] uppercase text-muted mb-3">
            Marja si TVA pe regim de marja
          </h2>
          {tva.isPending ? <Card><Skeleton rows={4} /></Card> : !tva.data ? (
            <Card><EmptyState title="Raportul nu s-a putut incarca" /></Card>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-4">
                <Card className="p-5">
                  <p className="text-[12.5px] text-muted">Vanzari</p>
                  <p className="mt-1.5 tnum text-[21px] leading-none">{lei(tva.data.summary.total_sales_ron)}</p>
                  <p className="mt-1 text-[12.5px] text-muted">{tva.data.summary.sales_count} facturi</p>
                </Card>
                <Card className="p-5">
                  <p className="text-[12.5px] text-muted">Costuri</p>
                  <p className="mt-1.5 tnum text-[21px] leading-none">{lei(tva.data.summary.total_costs_ron)}</p>
                  <p className="mt-1 text-[12.5px] text-muted">{tva.data.summary.purchase_count} facturi</p>
                </Card>
                <Card className="p-5">
                  <p className="text-[12.5px] text-muted">Comision brut</p>
                  <p className={'mt-1.5 tnum text-[21px] leading-none ' +
                    (tva.data.summary.comision_brut < 0 ? 'text-bad' : 'text-good')}>
                    {lei(tva.data.summary.comision_brut)}
                  </p>
                </Card>
                <Card className="p-5">
                  <p className="text-[12.5px] text-muted">TVA datorat</p>
                  <p className="mt-1.5 tnum text-[21px] leading-none">{lei(tva.data.summary.tva_datorat)}</p>
                  <p className="mt-1 text-[12.5px] text-muted">cota efectiva {tva.data.summary.effective_vat_rate}%</p>
                </Card>
              </div>
              {tva.data.summary.comision_brut < 0 && (
                <p className="mt-3 text-[13px] text-warn">
                  Costurile depasesc vanzarile in perioada — de regula inseamna facturi de furnizor
                  inregistrate inaintea celor de client, nu pierdere reala.
                </p>
              )}
              {tva.data.monthly.length > 1 && (
                <Card className="mt-4 overflow-hidden">
                  <table className="w-full text-[14px]">
                    <thead>
                      <tr className="text-left text-[11.5px] uppercase tracking-[0.1em] text-muted">
                        <th className="font-medium px-5 py-3">Luna</th>
                        <th className="font-medium px-5 py-3 text-right">Vanzari</th>
                        <th className="font-medium px-5 py-3 text-right">Costuri</th>
                        <th className="font-medium px-5 py-3 text-right">Comision</th>
                        <th className="font-medium px-5 py-3 text-right">TVA</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tva.data.monthly.map((m) => (
                        <tr key={m.month} className="border-t border-line">
                          <td className="px-5 py-2.5">{luna(m.month)}</td>
                          <td className="px-5 py-2.5 text-right tnum">{lei(m.sales)}</td>
                          <td className="px-5 py-2.5 text-right tnum">{lei(m.costs)}</td>
                          <td className="px-5 py-2.5 text-right tnum">{lei(m.comision_net)}</td>
                          <td className="px-5 py-2.5 text-right tnum">{lei(m.tva)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              )}
            </>
          )}
        </section>

        <section>
          <h2 className="text-[13px] font-semibold tracking-[0.08em] uppercase text-muted mb-3">
            Avansuri pe rezervari
          </h2>
          {av.isPending ? <Card><Skeleton rows={3} /></Card> : !av.data ? null : (
            <div className="grid gap-4 sm:grid-cols-3">
              <Card className="p-5">
                <p className="text-[12.5px] text-muted">Incasat de la clienti</p>
                <p className="mt-1.5 tnum text-[21px] leading-none text-good">{lei(av.data.totals.total_client_collected)}</p>
              </Card>
              <Card className="p-5">
                <p className="text-[12.5px] text-muted">Neplatit catre furnizori</p>
                <p className="mt-1.5 tnum text-[21px] leading-none text-warn">{lei(av.data.totals.total_supplier_unpaid)}</p>
              </Card>
              <Card className="p-5">
                <p className="text-[12.5px] text-muted">Avansuri in lucru</p>
                <p className="mt-1.5 tnum text-[21px] leading-none">{lei(av.data.totals.total_advances)}</p>
                <p className="mt-1 text-[12.5px] text-muted">{av.data.totals.count} rezervari</p>
              </Card>
            </div>
          )}
        </section>
      </div>
    </>
  )
}
